import React, { useState, useEffect } from 'react'
import { Modal, Button, Progress, Spin } from 'antd'
import _ from 'lodash'
import { deleteReuseEmbroidery, getReusedEmbroideryDesignOrders } from '../../../services/api/3dApi'
import ModalReusedEmbroideryDesignOrders from './ModalReusedEmbroideryDesignOrders'

const ModalBulkDeleteReuseEmbroidery = ({ visible, selectedItems = [], onClose, onDone }) => {
    const [phase, setPhase] = useState('confirm')
    const [processingList, setProcessingList] = useState([])
    const [isDone, setIsDone] = useState(false)
    const [reusedItemsMap, setReusedItemsMap] = useState({})
    const [isLoadingReused, setIsLoadingReused] = useState(false)

    const getBarcodeLabel = (item) =>
        `${_.get(item, 'org_barcode_item.barcode_number')}_${_.get(item, 'org_barcode_item.item_number')}`

    useEffect(() => {
        if (!visible || selectedItems.length === 0) return
        const fetchAll = async () => {
            setIsLoadingReused(true)
            const results = await Promise.all(
                selectedItems.map(async (item) => {
                    try {
                        const { data: res } = await getReusedEmbroideryDesignOrders(item._id)
                        const { success, data } = res || {}
                        const orders = (success && data?.orders) ? data.orders : []
                        return { id: item._id, items: orders, fetchError: !success }
                    } catch {
                        return { id: item._id, items: [], fetchError: true }
                    }
                })
            )
            const map = {}
            results.forEach(({ id, items, fetchError }) => {
                const allIds = items.map(i => i._id)
                map[id] = { items, allIds, selectItems: allIds, fetchError }
            })
            setReusedItemsMap(map)
            setIsLoadingReused(false)
        }
        fetchAll()
    }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleConfirmYes = async () => {
        setPhase('progress')
        const initial = selectedItems.map(item => ({ ...item, processing: true }))
        setProcessingList(initial)

        const updated = [...initial]
        for (let i = 0; i < selectedItems.length; i++) {
            const item = selectedItems[i]
            try {
                const payload = {}
                const entry = reusedItemsMap[item._id]
                if (entry && entry.selectItems.length > 0) {
                    payload.apply_embroidery_design_orders = entry.selectItems
                }

                const { data: res } = await deleteReuseEmbroidery(item._id, payload)
                const { success, error } = res
                if (!success) throw new Error(error)
                updated[i] = { ...item, processing: false, success: true }
            } catch (e) {
                updated[i] = { ...item, processing: false, error: e.message }
            }
            setProcessingList([...updated])
        }
        setIsDone(true)
    }

    const resetState = () => {
        setPhase('confirm')
        setProcessingList([])
        setIsDone(false)
        setReusedItemsMap({})
        setIsLoadingReused(false)
    }

    const handleClose = () => {
        resetState()
        onClose()
    }

    const handleDone = () => {
        resetState()
        onDone()
    }

    const setSelectItemsForId = (id) => (updater) => {
        setReusedItemsMap(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                selectItems: typeof updater === 'function'
                    ? updater(prev[id]?.selectItems || [])
                    : updater
            }
        }))
    }

    if (phase === 'confirm') {
        return (
            <Modal
                visible={visible}
                title="Xóa thiết kế thêu gốc"
                maskClosable={false}
                onCancel={handleClose}
                width={700}
                footer={[
                    <Button key="no" onClick={handleClose}>Cancel</Button>,
                    <Button key="yes" type="danger" onClick={handleConfirmYes} disabled={isLoadingReused || Object.values(reusedItemsMap).some(e => e.fetchError)}>
                        Delete
                    </Button>
                ]}
            >
                <p>Bạn có chắc chắn muốn xóa <b>{selectedItems.length}</b> thiết kế thêu gốc này không?</p>
                <Spin spinning={isLoadingReused}>
                    <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                        {selectedItems.map(item => {
                            const id = item._id
                            const entry = reusedItemsMap[id]
                            if (!entry) return null
                            if (entry.fetchError) return (
                                <div key={id} className="text-danger mb-2">
                                    <i className="fa fa-exclamation-triangle mr-1" />
                                    Không thể tải danh sách item reuse cho <b>{getBarcodeLabel(item)}</b>. Vui lòng thử lại.
                                </div>
                            )
                            if (!entry.items.length) return null

                            return (
                                <ModalReusedEmbroideryDesignOrders
                                    key={id}
                                    orderTemplate={`#${getBarcodeLabel(item)}`}
                                    items={entry.items}
                                    isActionDelete={true}
                                    selectItems={entry.selectItems}
                                    setSelectItems={setSelectItemsForId(id)}
                                    allIds={entry.allIds}
                                    ordersErr={{}}
                                />
                            )
                        })}
                    </div>
                </Spin>
            </Modal>
        )
    }

    return (
        <Modal
            visible={visible}
            title="Xóa thiết kế thêu gốc"
            closable={false}
            maskClosable={false}
            footer={isDone
                ? [<Button key="done" type="primary" onClick={handleDone}>Close</Button>]
                : null
            }
        >
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {processingList.map(item => (
                    <div key={item._id} className="mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="font-weight-bold">{getBarcodeLabel(item)}</span>
                            {item.success && <span className="text-success">✓ Xóa thành công</span>}
                            {item.error && <span className="text-danger">✗ {item.error}</span>}
                        </div>
                        <Progress
                            percent={100}
                            size="small"
                            status={item.processing ? 'active' : item.success ? 'success' : 'exception'}
                            showInfo={false}
                        />
                    </div>
                ))}
            </div>
        </Modal>
    )
}

export default ModalBulkDeleteReuseEmbroidery
